/**
 * The fetch-metadata gates shared by the broker and every consumer Worker:
 * is this request a top-level document navigation, and did it come from this
 * origin under a named same-origin policy?
 *
 * One source, two callers, so the two cannot drift apart on what they
 * accept: `packages/auth-consumer/src/auth.ts` gates its cookie-SETTING
 * routes (`/auth/sign-in`, `/auth/callback`) on it, and
 * `apps/auth/worker/lib/fetch-metadata.ts` re-exports it for both passes of
 * `/claim-reader`. Each caller's own header comment says what
 * the gate closes and what it leaves open on that route; this file owns only
 * the rule itself.
 *
 * THE RULE. `Sec-Fetch-Dest` and `Sec-Fetch-Mode` must each be absent or say
 * `document` / `navigate`. A subresource says otherwise — `<img>` is
 * `image`/`no-cors`, `fetch()` is `empty`/`cors`, a frame is
 * `iframe`/`navigate`, `<object>`/`<embed>` are `object`/`embed` — and is
 * refused. Absent headers pass: a browser too old to send fetch metadata
 * still gets a working flow, and each caller keeps its other checks for it.
 *
 * AND NO SPECULATIVE LOAD. A prefetch or prerender — speculation rules,
 * `<link rel=prerender>` — can be sent as `document` / `navigate` with the
 * page's cookies, which would pass the two checks above while the person sees
 * nothing. Such a request carries `Sec-Purpose` (`prefetch`, or
 * `prefetch;prerender`), and a navigation the person actually made never
 * does, so any `Sec-Purpose` is refused. The exact conditions under which
 * Chrome sends a same-site speculative load with cookies are NOT verified
 * here; the refusal is cheap and costs a real navigation nothing, so it does
 * not wait on that. A refused prefetch is simply discarded, and the click
 * that follows arrives without the header.
 *
 * MUST stay pure TypeScript with no runtime-specific imports, for
 * ./domainAllowlist.ts's reason: every consuming Worker bundles it.
 */

/**
 * Whether `headers` describe a top-level document navigation.
 *
 * `true` when `Sec-Fetch-Dest` and `Sec-Fetch-Mode` are each absent or say
 * `document` / `navigate` and no `Sec-Purpose` is present; `false` the moment
 * any of the three says otherwise.
 */
export function isTopLevelNavigation(headers: Headers): boolean {
  if (headers.get("sec-purpose") !== null) return false;
  const dest = headers.get("sec-fetch-dest");
  const mode = headers.get("sec-fetch-mode");
  if (dest !== null && dest !== "document") return false;
  if (mode !== null && mode !== "navigate") return false;
  return true;
}

/**
 * The same-origin rules in use, by name. Each differs from the others on at
 * least one header combination, so a caller picks the rule it means rather
 * than a nearby one; `fetchMetadata.test.ts` pins every combination.
 *
 * - `strictFormPost`: `Origin` must be present and exact, and
 *   `Sec-Fetch-Site`, when present, must say `same-origin`. For a form POST
 *   that spends authority: every browser that renders a form sends `Origin`
 *   on its POST, so a missing one is refused. `SameSite=Lax` alone would let
 *   a same-site sibling host submit the form with every cookie attached.
 * - `fetchMetadataFirst`: a present `Sec-Fetch-Site` decides alone
 *   (`same-origin` or refuse); without it, `Origin` must be present and exact.
 *   For a Bearer-authenticated fetch; old Safari omits fetch metadata but
 *   sends `Origin`.
 * - `eitherHeader`: `Origin` exact, or `Sec-Fetch-Site: same-origin`. Either
 *   header alone is proof, whatever the other says.
 * - `noForeignHeader`: each header, when present, must name this origin
 *   (`same-origin` / exact); a request with neither passes, and the caller
 *   relies on another gate (a JSON content type forcing a preflight) for it.
 * - `beacon`: `noForeignHeader`, plus `Sec-Fetch-Site: none`, which a
 *   `sendBeacon` or keepalive fetch can carry when it outlives its page.
 * - `fetchSiteOnly`: `Sec-Fetch-Site` absent, `same-origin` or `none`;
 *   `Origin` is not consulted. For a top-level GET navigation, which sends no
 *   `Origin`.
 */
export type SameOriginPolicy =
  | "strictFormPost"
  | "fetchMetadataFirst"
  | "eitherHeader"
  | "noForeignHeader"
  | "beacon"
  | "fetchSiteOnly";

interface OriginHeaders {
  readonly site: string | null;
  readonly origin: string | null;
}

const SAME_ORIGIN_RULES: Record<
  SameOriginPolicy,
  (headers: OriginHeaders, expectedOrigin: string) => boolean
> = {
  strictFormPost: ({ site, origin }, expected) =>
    origin === expected && (site === null || site === "same-origin"),
  fetchMetadataFirst: ({ site, origin }, expected) =>
    site !== null ? site === "same-origin" : origin === expected,
  eitherHeader: ({ site, origin }, expected) =>
    origin === expected || site === "same-origin",
  noForeignHeader: ({ site, origin }, expected) =>
    (site === null || site === "same-origin") &&
    (origin === null || origin === expected),
  beacon: ({ site, origin }, expected) =>
    (site === null || site === "same-origin" || site === "none") &&
    (origin === null || origin === expected),
  fetchSiteOnly: ({ site }) =>
    site === null || site === "same-origin" || site === "none",
};

/**
 * Whether `headers` show a request from `expectedOrigin` under `policy`.
 * `expectedOrigin` is a serialized origin (`https://host[:port]`), compared to
 * `Origin` exactly.
 */
export function isSameOriginRequest(
  headers: Pick<Headers, "get">,
  expectedOrigin: string,
  policy: SameOriginPolicy,
): boolean {
  return SAME_ORIGIN_RULES[policy](
    { site: headers.get("sec-fetch-site"), origin: headers.get("origin") },
    expectedOrigin,
  );
}
